import { useState, useEffect } from 'react';
import { Head, Link, router } from '@inertiajs/react';
import { AdventureAttempt, GrantedReward } from '@/types/adventures';
import { FlashMessage } from '@/components/ui';
import { EditorRenderer } from '@/components/Editor/EditorRenderer';

/**
 * Props for the AttemptShow page.
 */
interface Props {
    attempt: AdventureAttempt;
    statLabels: Record<string, string>;
    skillLabels: Record<string, string>;
}

/**
 * Adventure Attempt Show page - displays attempt details with actions.
 */
export default function AttemptShow({ attempt, statLabels, skillLabels }: Props) {
    const isInProgress = attempt.status.value === 'in_progress';
    const isCompleted = !isInProgress && attempt.status.value !== 'pending';

    return (
        <>
            <Head title={`${attempt.adventure?.name || 'Adventure'} - Attempt`} />

            {/* Flash messages for success/error notifications */}
            <FlashMessage />

            <div className="min-h-screen bg-slate-900 py-8 px-4">
                <div className="max-w-3xl mx-auto">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <Link
                            href="/adventures/attempts"
                            className="text-gray-400 hover:text-white transition-colors"
                        >
                            ← Back to My Adventures
                        </Link>
                    </div>

                    {/* Status Banner */}
                    <StatusBanner attempt={attempt} />

                    {/* Adventure Info */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
                        <h1 className="text-2xl font-bold text-white mb-2">
                            {attempt.adventure?.name || 'Unknown Adventure'}
                        </h1>

                        {/* Description rendered as Markdown */}
                        {attempt.adventure?.description && (
                            <div className="text-gray-300 mb-4">
                                <EditorRenderer content={attempt.adventure.description} />
                            </div>
                        )}

                        {/* Characters */}
                        <div className="mb-4">
                            <h3 className="text-sm font-medium text-gray-400 mb-2">Characters</h3>
                            <div className="flex flex-wrap gap-2">
                                {attempt.characters.map((char) => (
                                    <div
                                        key={char.id}
                                        className="px-3 py-1 bg-slate-700 rounded-lg text-white text-sm"
                                    >
                                        {char.name}
                                        {char.xp_awarded > 0 && (
                                            <span className="ml-2 text-green-400">+{char.xp_awarded} XP</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                            {attempt.started_at && (
                                <div>
                                    <span className="text-gray-500">Started:</span>{' '}
                                    {formatDate(attempt.started_at)}
                                </div>
                            )}
                            {attempt.completed_at && (
                                <div>
                                    <span className="text-gray-500">Completed:</span>{' '}
                                    {formatDate(attempt.completed_at)}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Start Dialogue Section - Narrative/Story Content */}
                    {attempt.adventure?.dialogue_start && (
                        <div className="bg-gradient-to-br from-purple-900/40 to-slate-800 border border-purple-700/50 rounded-lg p-6 mb-6 relative overflow-hidden">
                            {/* Decorative quote icon */}
                            <div className="absolute top-4 right-4 text-purple-500/20 text-6xl font-serif">"</div>

                            <h2 className="text-lg font-semibold text-purple-300 mb-4 flex items-center gap-2">
                                <span>📜</span>
                                <span>The Story Begins...</span>
                            </h2>

                            {/* Dialogue content with Markdown support */}
                            <div className="relative z-10 text-gray-200 leading-relaxed prose prose-invert prose-purple max-w-none">
                                <EditorRenderer content={attempt.adventure.dialogue_start} />
                            </div>
                        </div>
                    )}

                    {/* In Progress: Timer and Complete Button */}
                    {isInProgress && (
                        <InProgressSection attempt={attempt} />
                    )}

                    {/* Completed: Results */}
                    {isCompleted && (
                        <ResultsSection attempt={attempt} statLabels={statLabels} skillLabels={skillLabels} />
                    )}
                </div>
            </div>
        </>
    );
}

/**
 * StatusBanner - Shows current status with appropriate styling.
 */
function StatusBanner({ attempt }: { attempt: AdventureAttempt }) {
    const getBannerClass = (): string => {
        switch (attempt.status.value) {
            case 'in_progress':
                return 'bg-blue-900/50 border-blue-500';
            case 'success':
                return 'bg-green-900/50 border-green-500';
            case 'partial':
                return 'bg-yellow-900/50 border-yellow-500';
            case 'failure':
                return 'bg-red-900/50 border-red-500';
            default:
                return 'bg-slate-700 border-slate-500';
        }
    };

    return (
        <div className={`border-l-4 rounded-lg p-4 mb-6 ${getBannerClass()}`}>
            <div className="flex items-center gap-2">
                <span className="text-2xl">{attempt.status.icon}</span>
                <span className="text-lg font-medium text-white">{attempt.status.label}</span>
                {attempt.outcome && (
                    <span className={`ml-2 ${attempt.outcome.tailwindClass}`}>
                        {attempt.outcome.icon} {attempt.outcome.label}
                    </span>
                )}
            </div>
        </div>
    );
}

/**
 * InProgressSection - Timer and complete button for in-progress attempts.
 */
function InProgressSection({ attempt }: { attempt: AdventureAttempt }) {
    const [remainingSeconds, setRemainingSeconds] = useState(attempt.remaining_seconds);
    const [isCompleting, setIsCompleting] = useState(false);
    const isReady = attempt.is_ready_to_complete || remainingSeconds <= 0;

    // Countdown timer
    useEffect(() => {
        if (remainingSeconds <= 0) return;

        const timer = setInterval(() => {
            setRemainingSeconds((prev) => Math.max(0, prev - 1));
        }, 1000);

        return () => clearInterval(timer);
    }, [remainingSeconds]);

    // Handle complete button
    const handleComplete = () => {
        setIsCompleting(true);
        router.post(`/adventures/attempts/${attempt.id}/complete`, {}, {
            onFinish: () => setIsCompleting(false),
        });
    };

    // Format time
    const hours = Math.floor(remainingSeconds / 3600);
    const minutes = Math.floor((remainingSeconds % 3600) / 60);
    const secs = remainingSeconds % 60;

    const timeString = hours > 0
        ? `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
        : `${minutes}:${secs.toString().padStart(2, '0')}`;

    return (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-4">⏳ Adventure in Progress</h2>

            {isReady ? (
                <div className="text-center">
                    <div className="text-green-400 text-xl mb-4">
                        ✓ Your characters have returned!
                    </div>
                    <p className="text-gray-300 mb-6">
                        Complete the adventure to see your results and claim rewards.
                    </p>
                    <button
                        onClick={handleComplete}
                        disabled={isCompleting}
                        className={`px-8 py-3 rounded-lg font-medium text-lg transition-colors ${
                            isCompleting
                                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}
                    >
                        {isCompleting ? 'Rolling...' : '🎲 Complete Adventure'}
                    </button>
                </div>
            ) : (
                <div className="text-center">
                    <div className="text-5xl font-mono text-blue-400 mb-2">{timeString}</div>
                    <p className="text-gray-400">until your characters return</p>

                    {/* Progress bar */}
                    <div className="mt-6 bg-slate-700 rounded-full h-2 overflow-hidden">
                        <div
                            className="bg-blue-500 h-full transition-all duration-1000"
                            style={{
                                width: `${Math.max(0, 100 - (remainingSeconds / (attempt.adventure?.total_difficulty || 1) / 600) * 100)}%`,
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * ResultsSection - Shows results for completed attempts.
 */
function ResultsSection({
    attempt,
}: {
    attempt: AdventureAttempt;
    statLabels: Record<string, string>;
    skillLabels: Record<string, string>;
}) {
    const [isClaiming, setIsClaiming] = useState(false);

    // Handle claim rewards
    const handleClaim = () => {
        setIsClaiming(true);
        router.post(`/adventures/attempts/${attempt.id}/claim`, {}, {
            onFinish: () => setIsClaiming(false),
        });
    };

    return (
        <div className="space-y-6">
            {/* Roll Results */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                <h2 className="text-lg font-semibold text-white mb-4">🎲 Roll Results</h2>

                <div className="flex items-center justify-center gap-8 mb-4">
                    <div className="text-center">
                        <div className="text-4xl font-bold text-white">{attempt.roll_result}</div>
                        <div className="text-sm text-gray-400">Your Roll</div>
                    </div>
                    <div className="text-2xl text-gray-500">vs</div>
                    <div className="text-center">
                        <div className="text-4xl font-bold text-gray-400">{attempt.success_chance}%</div>
                        <div className="text-sm text-gray-400">Success Chance</div>
                    </div>
                </div>

                {attempt.is_critical && (
                    <div className="text-center text-yellow-400 font-medium animate-pulse">
                        ⭐ Critical Roll! (Doubles) - Double Rewards! ⭐
                    </div>
                )}
            </div>

            {/* Dialogue Section */}
            <DialogueSection attempt={attempt} />

            {/* Rewards Section - show for completed attempts */}
            {attempt.status?.value !== 'pending' && attempt.status?.value !== 'in_progress' && (
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                    <h2 className="text-lg font-semibold text-white mb-4">🎁 Rewards</h2>

                    {attempt.rewards_granted && attempt.rewards_granted.length > 0 ? (
                        <>
                            <div className="space-y-3 mb-6">
                                {attempt.rewards_granted.map((reward, index) => (
                                    <RewardItem key={index} reward={reward} />
                                ))}
                            </div>

                            {!attempt.rewards_claimed ? (
                                <button
                                    onClick={handleClaim}
                                    disabled={isClaiming}
                                    className={`w-full py-3 rounded-lg font-medium transition-colors ${
                                        isClaiming
                                            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                            : 'bg-purple-600 hover:bg-purple-700 text-white'
                                    }`}
                                >
                                    {isClaiming ? 'Claiming...' : '✨ Claim Rewards'}
                                </button>
                            ) : (
                                <div className="text-center text-green-400 py-3">
                                    ✓ Rewards Claimed
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center text-gray-400 py-4">
                            {attempt.outcome?.value === 'failure'
                                ? 'No rewards earned from this attempt.'
                                : 'No rewards were granted for this adventure.'}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * DialogueSection - Displays adventure dialogue based on outcome.
 */
function DialogueSection({ attempt }: { attempt: AdventureAttempt }) {
    const isSuccess = attempt.outcome?.value === 'success' || attempt.outcome?.value === 'critical_success';
    const isPartial = attempt.outcome?.value === 'partial_success';

    // Determine which dialogue to show (partial success still shows success dialogue per spec)
    const dialogue = isSuccess || isPartial
        ? attempt.adventure?.dialogue_success
        : attempt.adventure?.dialogue_failure;

    if (!dialogue) return null;

    // Get styling based on outcome
    const getBorderColor = (): string => {
        if (isSuccess) return 'border-green-500';
        if (isPartial) return 'border-yellow-500';
        return 'border-red-500';
    };

    const getBackgroundColor = (): string => {
        if (isSuccess) return 'bg-green-900/20';
        if (isPartial) return 'bg-yellow-900/20';
        return 'bg-red-900/20';
    };

    const getIcon = (): string => {
        if (isSuccess) return '🎉';
        if (isPartial) return '😅';
        return '😔';
    };

    return (
        <div className={`${getBackgroundColor()} border-l-4 ${getBorderColor()} rounded-lg p-6`}>
            <div className="flex items-start gap-4">
                <span className="text-3xl">{getIcon()}</span>
                <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-400 mb-2">
                        {isSuccess ? 'Victory!' : isPartial ? 'Close Call...' : 'Defeat...'}
                    </h3>
                    <p className="text-gray-200 text-lg italic leading-relaxed">
                        "{dialogue}"
                    </p>
                </div>
            </div>
        </div>
    );
}

/**
 * RewardItem - Displays a single reward.
 */
function RewardItem({ reward }: { reward: GrantedReward }) {
    if (!reward) return null;

    return (
        <div className="flex items-center justify-between bg-slate-700/50 rounded-lg p-3">
            <div className="flex items-center gap-3">
                {reward.type === 'item' && (
                    <>
                        <span className="text-xl">🎁</span>
                        <div>
                            <div className="text-purple-300 font-medium">{reward.item_name || 'Item'}</div>
                            {reward.quality !== undefined && (
                                <div className="text-xs text-gray-400">Quality: {reward.quality}%</div>
                            )}
                        </div>
                    </>
                )}
                {reward.type === 'material' && (
                    <>
                        <span className="text-xl">📦</span>
                        <div>
                            <div className="text-amber-300 font-medium">{reward.material_type}</div>
                            <div className="text-xs text-gray-400">x{reward.quantity}</div>
                        </div>
                    </>
                )}
                {reward.type === 'xp' && (
                    <>
                        <span className="text-xl">⭐</span>
                        <div>
                            <div className="text-green-300 font-medium">{reward.total_xp} XP</div>
                            <div className="text-xs text-gray-400">
                                {reward.xp_per_character} per character
                            </div>
                        </div>
                    </>
                )}
                {reward.type === 'blueprint' && (
                    <>
                        <span className="text-xl">📜</span>
                        <div>
                            <div className="text-cyan-300 font-medium">{reward.item_name || 'Blueprint'}</div>
                            {reward.quality !== undefined && (
                                <div className="text-xs text-gray-400">Quality: {reward.quality}%</div>
                            )}
                        </div>
                    </>
                )}
                {reward.type === 'reputation' && (
                    <>
                        <span className="text-xl">🏛️</span>
                        <div>
                            <div className="text-blue-300 font-medium">{reward.amount >= 0 ? '+' : ''}{reward.amount} Reputation</div>
                            <div className="text-xs text-gray-400">
                                {reward.enclave_name}{reward.new_level ? ` → ${reward.new_level}` : ''}
                            </div>
                        </div>
                    </>
                )}
                {/* Fallback for unknown reward types */}
                {!['item', 'material', 'xp', 'blueprint', 'reputation'].includes(reward.type) && (
                    <>
                        <span className="text-xl">🎁</span>
                        <div>
                            <div className="text-gray-300 font-medium">{reward.type}</div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

/**
 * Format ISO date string to readable format.
 */
function formatDate(isoString: string): string {
    const date = new Date(isoString);
    return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

